<template>
  <div class="client-info-form">
    <v-form ref="form">
      <div class="client-info-form-row">
        <v-text-field
          class="left-input"
          v-model="firstName"
          @change="formChanged"
          :rules="[rules.required]"
          :label="$t('registration.client.info.firstname')"
          :placeholder="$t('registration.client.info.firstname.placeholder')"
          dense
          outlined
          required
          autofocus
        />
        <v-text-field
          v-model="lastName"
          @change="formChanged"
          :rules="[rules.required]"
          :label="$t('registration.client.info.lastname')"
          :placeholder="$t('registration.client.info.lastname.placeholder')"
          dense
          outlined
          required
        />
      </div>
      <v-text-field
        v-model="jobTitle"
        @change="formChanged"
        :rules="[rules.required]"
        :label="$t('registration.client.info.jobtitle')"
        :placeholder="$t('registration.client.info.jobtitle.placeholder')"
        dense
        outlined
        required
      />
      <vue-tel-input-vuetify
        v-model="phoneNumber"
        @change="formChanged"
        mode="international"
        :placeholder="this.$t('registration.client.info.phonenumber.placeholder')"
        :label="$t('registration.client.info.phonenumber')"
        :selectLabel="$t('registration.client.info.phonenumber.country')"
        outlined
        dense
        required
      />
      <v-checkbox v-model="termsAndConditionsAccepted" dense hide-details :rules="[rules.required]">
        <template slot="label">
          <div class="checkbox-title">
            <span>
              {{ $t('registration.client.info.agree.with') }}
            </span>
            <v-btn link @click.stop dense text @click="toggleTermsOfUsePopup">
              {{ this.$t('registration.client.info.terms.of.use') }}
            </v-btn>
          </div>
        </template>
      </v-checkbox>
    </v-form>
    <terms-of-use-popup :visible="showTermsOfUsePopup" @closed="showTermsOfUsePopup = false" />
  </div>
</template>

<script>
import { rules } from '@/utils/validationUtils';
import TermsOfUsePopup from '@/components/popup/TermsOfUsePopup.vue';

export default {
  name: 'SPFreelancerClientInfoForm',
  components: {
    TermsOfUsePopup,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      valid: this.value.valid,
      lastName: this.value.lastName,
      firstName: this.value.firstName,
      jobTitle: this.value.jobTitle,
      phoneNumber: this.value.phoneNumber,
      termsAndConditionsAccepted: this.value.termsAndConditionsAccepted,
      showTermsOfUsePopup: false,
    };
  },
  computed: {
    rules() {
      return {
        ...rules,
        email: v => rules.email(v) || this.$t('registration.client.info.email.is.not.valid'),
        phone: v => rules.phone(v) || this.$t('registration.client.info.phone.number.is.not.valid'),
        required: v => rules.required(v) || this.$t('required'),
        matchWithPassword: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          v === this.password || this.$t('registration.client.info.passwords.must.be.same'),
        requiredNumber: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredNumber(v) || this.$t('registration.client.info.passwords.at.least.one.number'),
        requiredUpperCase: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredUpperCase(v) || this.$t('registration.client.info.passwords.at.least.one.capital'),
        requiredLowerCase: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredLowerCase(v) || this.$t('registration.client.info.passwords.at.least.one.lower'),
        requiredSpecialSymbol: v =>
          // eslint-disable-next-line implicit-arrow-linebreak
          rules.requiredSpecialSymbol(v) || this.$t('registration.client.info.passwords.at.least.one.symbol'),
      };
    },
  },
  methods: {
    toggleTermsOfUsePopup() {
      this.showTermsOfUsePopup = !this.showTermsOfUsePopup;
    },
    formChanged() {
      this.valid = this.$refs.form.inputs.every(input => input.valid);

      this.$emit('changed', {
        valid: this.valid,
        lastName: this.lastName,
        firstName: this.firstName,
        jobTitle: this.jobTitle,
        phoneNumber: this.phoneNumber,
        termsAndConditionsAccepted: this.termsAndConditionsAccepted,
      });
    },
  },
  watch: {
    lastName() {
      this.formChanged();
    },
    firstName() {
      this.formChanged();
    },
    jobTitle() {
      this.formChanged();
    },
    phoneNumber() {
      this.formChanged();
    },
    termsAndConditionsAccepted() {
      this.formChanged();
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot {
  height: rem(32);
}

.vue-tel-input-vuetify .v-text-field--outlined .v-select__selections .vti__flag {
  margin-left: rem(20) !important;
}

.client-info-form {
  .v-input__icon--append {
    margin-top: 0 !important;
  }

  .v-autocomplete {
    input {
      text-overflow: ellipsis;
    }
  }

  .v-text-field__suffix {
    color: $auth-text-primary;
    font-size: rem(14);
    letter-spacing: 0;
    line-height: rem(16);
    padding-right: rem(10);
  }

  .checkbox-title .v-btn {
    padding: 0 rem(2);
  }

  .checkbox-title .v-btn__content {
    color: $auth-text-secondary;
    font-size: rem(14);
    letter-spacing: 0;
    line-height: rem(16);
    text-transform: capitalize;
  }
}
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.client-info-form {
  display: flex;
  flex-direction: column;
  min-width: rem(356);

  .v-input--checkbox {
    margin-top: 0;
  }

  .v-autocomplete {
    input {
      text-overflow: ellipsis;
    }
  }
}

.client-info-form-row {
  display: flex;
}

.left-input {
  margin-right: rem(8);
}

.checkbox-title {
  display: flex;
  align-items: center;

  span {
    font-size: rem(14);
    letter-spacing: 0;
    line-height: rem(16);
  }
}
</style>
